import { AgoraVideoPlayer } from "agora-rtc-react";
import html2canvas from "html2canvas";
import { createRef, useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useScreenshot } from "use-react-screenshot";
import { axiosPostData } from "../../../api/APIinstance";
import {
  generateUserId,
  compressFile,
} from "../../ProjectComponents/Common/Functions/CommonFunctions";
import SweetAlert from "sweetalert2";

export default function Video(props) {
  const {
    users,
    tracks,
    videoTrackScreenShare,
    client,
    switchedTrack,
    localVideoKey,
  } = props;

  const [enlargeUser, setEnlargeUser] = useState(0);
  const [image, takeScreenshot] = useScreenshot();
  const [status, setStatus] = useState(null);
  const { id } = useParams();
  const ref = createRef(null);
  const [geolocation, setGeoLocation] = useState();
  const [gridSpacing, setGridSpacing] = useState(12);

  const user = localStorage.getItem("user");

  // ── Layout state ──────────────────────────────────────────────────────────
  //
  // isLocalInMain: true  → local camera is in the MAIN (large) screen
  //                false → local camera is in the PiP (top-left box)   [default]
  //
  // When screen share starts, we put the screen share track wherever the
  // local feed currently is, and move the local feed to the other slot.
  //
  const [isLocalInMain, setIsLocalInMain] = useState(false);

  // mainTrack    = what plays in the large fixed screen area
  // pipTrack     = what plays in the small top-left PiP box
  const [mainTrack, setMainTrack] = useState(null);   // null = show remote user
  const [pipTrack, setPipTrack] = useState(tracks[1]); // starts as local camera

  // Remember where local was before screen share so we can restore correctly
  const wasLocalInMainBeforeShare = useRef(false);

  // ── Sync tracks when screen share starts / stops ──────────────────────────
  useEffect(() => {
    if (videoTrackScreenShare !== null) {
      // ── Screen share STARTING ──────────────────────────────────────────────
      // Record where local feed is right now
      wasLocalInMainBeforeShare.current = isLocalInMain;

      // if (isLocalInMain) {
      //   // Local is in MAIN → put screen share in MAIN, push local to PiP
      //   setMainTrack(videoTrackScreenShare);
      //   setPipTrack(tracks[1]);
      //   // isLocalInMain stays true conceptually (screen share replaced local
      //   // in main), but for swap logic we treat screen-share-in-main as the
      //   // "local slot" so restoration works correctly — no state change needed.
      // } else {
      //   // Local is in PiP → put screen share in PiP (replace local camera)
      //   setPipTrack(videoTrackScreenShare);
      //   // Main stays showing remote user (mainTrack = null)
      // }

      // REPLACE this entire if/else inside the "Screen share STARTING" block:

      if (isLocalInMain) {
        // Local is in MAIN → put screen share in MAIN
        // PiP already has the remote user — DO NOT touch it
        setMainTrack(videoTrackScreenShare);
        // ← removed setPipTrack(tracks[1]) — this was overwriting remote user
      } else {
        // Local is in PiP → put screen share in PiP (replace local camera)
        setPipTrack(videoTrackScreenShare);
        // Main already has remote user (mainTrack = null) — DO NOT touch it
      }
    } else {
      // ── Screen share STOPPING ─────────────────────────────────────────────
      if (wasLocalInMainBeforeShare.current) {
        // Screen share was in MAIN → restore local camera to MAIN
        setMainTrack(tracks[1]);
        // PiP stays as local camera (already set) or remote — keep as-is
      } else {
        // Screen share was in PiP → restore local camera to PiP
        setPipTrack(tracks[1]);
        setMainTrack(null); // restore remote user to main
      }
    }
  }, [videoTrackScreenShare]); // intentionally minimal deps — see note below
  // NOTE: We don't include isLocalInMain/tracks in deps because this effect
  // must only fire on screen share state changes, not on every swap click.
  // isLocalInMain is read synchronously at the moment screen share starts
  // which is always correct since the effect fires after the state update.

  // ── Reset enlargeUser if the enlarged user left ───────────────────────────
  useEffect(() => {
    if (enlargeUser >= users.length && users.length > 0) {
      setEnlargeUser(0);
    }
  }, [users, enlargeUser]);

  useEffect(() => {
    setGridSpacing(Math.max(Math.floor(12 / (users.length + 1)), 4));
  }, [users, tracks]);

  // ── Screenshot helpers ────────────────────────────────────────────────────
  async function takeSSReturn() {
    let div = document.getElementById("screenshot");
    let canvas = await html2canvas(div);
    let a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    const FileName = generateUserId();
    if (!!a.href) {
      let response = await fetch(a.href, { headers: { "Content-Type": "image/png" } });
      let fileObject = await response.blob();
      return new File([fileObject], `${FileName}`, { type: "image/png" });
    }
  }

  function showLocationError() {
    SweetAlert.fire({
      title: "Unable to capture location, please enable browser location, refresh & try again!",
      icon: "error",
    });
  }

  function checkLocationPermission() {
    navigator.permissions.query({ name: "geolocation" }).then(result => {
      if (result.state === "granted") {
        getImage();
      } else if (result.state === "prompt") {
        result.onchange = () => {
          if (result.state === "granted") getImage();
          if (result.state === "denied") showLocationError();
        };
      } else {
        showLocationError();
      }
    });
  }

  const getImage = async () => {
    let iconToHide = document.getElementById("ssIconOnVideoCall");
    const fileName = generateUserId();
    const ssFile = await takeSSReturn();

    if (geolocation !== undefined) {
      iconToHide?.classList.add("d-none");
      setStatus(null);
      compressFile(ssFile).then(async (imgres) => {
        const formData = new FormData();
        formData.append("checklist_file", imgres);
        formData.append("fileType", "Image");
        formData.append("withAnswer", "True");
        formData.append("survey", id);
        formData.append("name", fileName);
        formData.append("lat", geolocation.latitude.toFixed(6));
        formData.append("long", geolocation.longitude.toFixed(6));
        formData.append("createdBy", localStorage.getItem("vdi_User_Id"));
        await axiosPostData("CheckListAttachment/", formData);
        toast.success("Screenshot Taken ...", { autoClose: 2000, theme: "light" });
        iconToHide?.classList.remove("d-none");
      }, err => console.log("compress error", err));
    } else {
      toast.error(
        "Unable to capture location, please enable browser location, refresh & try again!",
        { autoClose: 4000, theme: "light" }
      );
    }
  };

  // ── Swap handler: called when user clicks the PiP box ────────────────────
  //
  // Swaps whatever is in PiP ↔ Main.
  // Also updates isLocalInMain so screen share knows where local feed is.
  //
  const handlePipClick = () => {
    // Don't allow swap while screen sharing — screen share placement is fixed
    // to the local slot until sharing stops
    if (videoTrackScreenShare !== null) return;

    if (!isLocalInMain) {
      // Local is in PiP → move local to MAIN, remote to PiP
      setMainTrack(tracks[1]);                              // local cam in main
      setPipTrack(users[enlargeUser]?.videoTrack ?? null);  // remote in pip
      setIsLocalInMain(true);
    } else {
      // Local is in MAIN → move remote to MAIN, local to PiP
      setMainTrack(null);          // null = render remote user normally in main
      setPipTrack(tracks[1]);      // local cam back in pip
      setIsLocalInMain(false);
    }
  };

  // ── Main screen renderer ──────────────────────────────────────────────────
  const renderMainScreen = () => {
    // No other users — always show local or screen share full screen
    if (users.length === 0) {
      return (
        <AgoraVideoPlayer
          key={localVideoKey}
          className="vid"
          videoTrack={
            videoTrackScreenShare !== null
              ? videoTrackScreenShare
              : tracks[1]
          }
        />
      );
    }

    // mainTrack set explicitly (local cam swapped to main, or screen share in main)
    if (mainTrack !== null) {
      return (
        <AgoraVideoPlayer
          key={`main-${localVideoKey}`}
          className="vid"
          videoTrack={mainTrack}
        />
      );
    }

    // Default: show remote user in main
    return users[enlargeUser] ? (
      <AgoraVideoPlayer
        className="vid remote_user"
        videoTrack={users[enlargeUser].videoTrack}
        key={users[enlargeUser].uid}
      />
    ) : (
      <div className="vid d-flex align-items-center justify-content-center text-light">
        User disconnected...
      </div>
    );
  };

  // ── PiP renderer ─────────────────────────────────────────────────────────
  const renderPip = () => {
    if (!pipTrack) return null;
    return (
      <AgoraVideoPlayer
        key={`pip-${localVideoKey}`}
        className="vid"
        videoTrack={pipTrack}
        onClick={handlePipClick}
        style={{ cursor: videoTrackScreenShare !== null ? "default" : "pointer" }}
      />
    );
  };

  return (
    <>
      {/* ── Main video area ─────────────────────────────────────────────── */}
      <div
        ref={ref}
        style={{ background: "grey", height: "100%", width: "100%" }}
        id="screenshot"
      >
        {renderMainScreen()}

        {/* Screenshot icon */}
        {user === "SiteSurveyor" ? null : users.length ? (
          <i
            className="fa fa-camera text-light"
            onClick={() => checkLocationPermission()}
            style={{
              position: "relative",
              top: "-95%",
              left: "90%",
              fontSize: "20px",
              cursor: "pointer",
            }}
            id="ssIconOnVideoCall"
          />
        ) : (
          <i
            className="fa fa-camera text-secondary"
            style={{ position: "absolute", top: "15px", right: "15px", fontSize: "20px" }}
            id="ssIconOnVideoCall"
          />
        )}
      </div>

      {/* ── PiP box — only when other users are present ─────────────────── */}
      {users.length > 0 && (
        <div
          style={{
            position: "absolute",
            left: "0px",
            top: "0px",
            height: "200px",
            width: "200px",
          }}
        >
          {renderPip()}
        </div>
      )}

      {/* ── Other users thumbnail strip ──────────────────────────────────── */}
      {users.length > 0 && (
        <div className="row" id="otherusersrow" style={{ marginTop: "0" }}>
          {users.map((user, i) => {
            if (i !== enlargeUser && user.videoTrack) {
              return (
                <div className="col-md-4 mb-2" key={user.uid}>
                  <div className="other_users cursor_pointer d-block">
                    <AgoraVideoPlayer
                      className="vid"
                      videoTrack={user.videoTrack}
                      key={user.uid}
                      onClick={() => setEnlargeUser(i)}
                    />
                  </div>
                </div>
              );
            }
            return null;
          })}
        </div>
      )}
    </>
  );
}